import writeXlsxFileDefault from 'write-excel-file';
import { supabase as defaultSupabase } from './supabase';

export const BOOKING_COLUMNS = [
  ['id', 'string'], ['property_id', 'string'], ['room_id', 'string'], ['guest_name', 'string'],
  ['phone', 'string'], ['phone_key', 'string'], ['email', 'string'],
  ['check_in', 'date'], ['check_out', 'date'],
  ['price', 'number'], ['price_per_night', 'number'], ['payment_status', 'string'],
  ['deposit_amount', 'number'], ['source', 'string'], ['adults', 'number'],
  ['children', 'jsonArr'], ['extras', 'jsonArr'], ['status', 'string'],
  ['created_at', 'ts'], ['preferred_contact', 'string'], ['breakfast_included', 'bool'],
  ['breakfast_price', 'number'], ['extra_beds', 'number'],
  ['commission_pct_snapshot', 'number'], ['vcc_fee_pct_snapshot', 'number'],
  ['extra_bed_price_snapshot', 'number'], ['message_log', 'jsonObj'],
  ['note', 'string'], ['lang', 'string'],
];

export const GUEST_NOTE_COLUMNS = [
  ['id', 'string'], ['property_id', 'string'], ['phone_key', 'string'], ['note', 'string'],
  ['updated_at', 'ts'], ['preferred_contact', 'string'], ['trusted', 'bool'], ['lang', 'string'],
];

export const ROOM_COLUMNS = [
  ['id', 'string'], ['property_id', 'string'], ['name', 'string'], ['type', 'string'],
  ['capacity', 'number'], ['base_price', 'number'], ['seasons', 'jsonArr'], ['sort_order', 'number'],
  ['max_adults', 'number'], ['max_children', 'number'], ['child_surcharge_per_night', 'number'],
  ['archived', 'bool'],
];

export const PROPERTY_EXCLUDED = new Set([
  'stripe_restricted_key', 'stripe_customer_id', 'stripe_subscription_id', 'admin_notes',
  'plan_status', 'trial_started_at', 'trial_ends_at', 'grace_ends_at', 'last_seen_at',
  'onboarding_step', 'setup_badges_dismissed',
]);

function isoUtc(ts) {
  if (ts == null || ts === '') return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function encodeCell(value, type) {
  switch (type) {
    case 'number':  return value == null ? { value: null } : { value: Number(value), type: Number };
    case 'bool':    return value == null ? { value: null } : { value: !!value, type: Boolean };
    case 'date':    return value == null ? { value: null } : { value: String(value), type: String };
    case 'ts': {
      const s = isoUtc(value);
      return s == null ? { value: null } : { value: s, type: String };
    }
    case 'jsonArr': return { value: JSON.stringify(value == null ? [] : value), type: String };
    case 'jsonObj': return { value: JSON.stringify(value == null ? {} : value), type: String };
    default:        return value == null ? { value: null } : { value: String(value), type: String };
  }
}

function headerRow(columns) {
  return columns.map(([name]) => ({ value: name, fontWeight: 'bold' }));
}

function dataRows(rows, columns) {
  return (rows || []).map(row => columns.map(([name, type]) => encodeCell(row[name], type)));
}

export function buildBookingsSheet(rows)   { return [headerRow(BOOKING_COLUMNS), ...dataRows(rows, BOOKING_COLUMNS)]; }
export function buildGuestNotesSheet(rows) { return [headerRow(GUEST_NOTE_COLUMNS), ...dataRows(rows, GUEST_NOTE_COLUMNS)]; }
export function buildRoomsSheet(rows)      { return [headerRow(ROOM_COLUMNS), ...dataRows(rows, ROOM_COLUMNS)]; }

export function buildPropertySheet(propertyRow) {
  const row = propertyRow || {};
  const keys = Object.keys(row).filter(k => !PROPERTY_EXCLUDED.has(k));
  const header = keys.map(k => ({ value: k, fontWeight: 'bold' }));
  const data = keys.map(k => {
    const v = row[k];
    if (v == null) return { value: null };
    if (v instanceof Date) return { value: isoUtc(v), type: String };
    if (typeof v === 'object') return { value: JSON.stringify(v), type: String };
    if (typeof v === 'number') return { value: v, type: Number };
    if (typeof v === 'boolean') return { value: v, type: Boolean };
    if (k === 'created_at') return { value: isoUtc(v), type: String };
    return { value: String(v), type: String };
  });
  return [header, data];
}

export function backupFileName(property, now = new Date()) {
  const slug = (property && property.slug) || 'hotel';
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `tavelo-backup-${slug}-${y}-${m}-${d}.xlsx`;
}

export async function downloadBackup(property, deps = {}) {
  const sb = deps.supabase || defaultSupabase;
  const write = deps.writeXlsxFile || writeXlsxFileDefault;
  const now = deps.now || new Date();
  const propId = property && property.id;

  const [bk, notes, rms, prop] = await Promise.all([
    sb.from('bookings').select('*').eq('property_id', propId),
    sb.from('guest_notes').select('*').eq('property_id', propId),
    sb.from('rooms').select('*').eq('property_id', propId),
    sb.from('properties').select('*').eq('id', propId),
  ]);

  const failed = [bk, notes, rms, prop].find(r => r && r.error);
  if (failed) throw failed.error;

  const sheets = [
    buildBookingsSheet(bk.data),
    buildGuestNotesSheet(notes.data),
    buildRoomsSheet(rms.data),
    buildPropertySheet(prop.data?.[0]),
  ];

  await write(sheets, {
    sheets: ['Bookings', 'Guest notes', 'Rooms', 'Property'],
    fileName: backupFileName(property, now),
  });
}
